import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { signUpWithEmail, signInWithGoogle, upgradeToCreator } from '@/lib/firebase/auth';
import EmailSignUpForm from '@/components/EmailSignUpForm';
import type { SignUpData } from '@/lib/auth/validation';
import { toast } from 'sonner';
import AuthLayout, { AuthDivider, AuthSecondary } from '@/components/AuthLayout';
import { usePageMeta } from '@/hooks/usePageMeta';
import SignupExitIntentPrompt from '@/components/SignupExitIntentPrompt';

export default function SignUpCreator() {
  usePageMeta('Create your fund page', 'Set up a free athlete fund page on Sport Fund in under two minutes.');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const handlePostSignupUpgrade = async (user: any) => {
    try {
      await upgradeToCreator(user.uid);
      toast.success('Welcome, athlete! Redirecting to your dashboard…');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to mark account as athlete');
    }
  };

  const handleEmailSignUp = async (data: SignUpData) => {
    setIsLoading(true);
    setError(undefined);
    try {
      const user = await signUpWithEmail(data);
      await handlePostSignupUpgrade(user);
      navigate('/creator');
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const user = await signInWithGoogle();
      await handlePostSignupUpgrade(user);
      navigate('/creator');
    } catch (err: any) {
      setError(err.message || 'Google Sign-In failed');
    }
  };

  return (
    <>
    <SignupExitIntentPrompt />
    <AuthLayout
      title="Create your free fund page."
      subtitle="Two minutes from here to a live fund page. Your story, your sport, your goal — and a link your community can actually use to back you."
      footer={
        <div className="space-y-3">
          <p className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-slate-400" />
            <span>Secure authentication. By signing up, you agree to our{" "}<Link to="/terms" className="underline">Terms of Service</Link>,{" "}<Link to="/acceptable-use" className="underline">Acceptable Use Policy</Link> and{" "}<Link to="/privacy" className="underline">Privacy Policy</Link>.</span>
          </p>
          <p>
            Already have an account?{' '}
            <Link to="/signin" className="font-semibold text-blue-700 hover:text-blue-800">
              Sign in
            </Link>{' '}
            • Want to support an athlete instead?{' '}
            <Link to="/signup" className="font-semibold text-blue-700 hover:text-blue-800">
              Supporter signup
            </Link>
          </p>
        </div>
      }
    >
      <EmailSignUpForm onSubmit={handleEmailSignUp} isLoading={isLoading} error={error} showHowDidYouHear />
      <AuthDivider label="Or continue with" />
      <AuthSecondary onClick={handleGoogleSignIn}>
        <img
          src="/google.svg"
          alt=""
          className="h-5 w-5"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = 'https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg';
          }}
        />
        Continue with Google
      </AuthSecondary>
    </AuthLayout>
    </>
  );
}
